import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
  Icon,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@flatfile/baseline'
import { useEffect, useCallback, useState } from 'react'
import { AppCardsDisplay } from './AppCardsDisplay'

export interface CreateAppPathProps {
  mini?: boolean
  externalTrigger?: (triggerFn: () => void) => void
  hideUI?: boolean
}

export default function CreateAppPath({ mini = false, externalTrigger, hideUI = false }: CreateAppPathProps) {
  const [createAppModalOpen, setCreateAppModalOpen] = useState(false)
  const openModal = () => setCreateAppModalOpen(true)
  const closeModal = () => setCreateAppModalOpen(false)

  // Create a stable callback that doesn't change on every render
  const stableOpenModal = useCallback(() => {
    openModal()
  }, [])

  // Register the external trigger function if provided
  useEffect(() => {
    if (externalTrigger) {
      externalTrigger(stableOpenModal)
    }
  }, [externalTrigger, stableOpenModal])

  const CardView = () => <AppCardsDisplay onAnyAction={closeModal} />

  const toggleModal = () => (createAppModalOpen ? closeModal() : openModal())

  return (
    <>
      <Dialog open={createAppModalOpen} onOpenChange={setCreateAppModalOpen}>
        <DialogContent
          className='p-0 w-screen h-screen inset-0 m-0 translate-x-0 translate-y-0 top-0 left-0 border-none flex flex-col gap-0 max-w-none'
          fullscreen
        >
          <div className='flex justify-between items-center px-6 py-4'>
            <DialogTitle className='sr-only'>Add an App</DialogTitle>
            <DialogClose>
              <Icon name='cross-large' size={16} />
            </DialogClose>
          </div>
          <div className='flex flex-col h-full'>
            <div className='px-6 pt-6 pb-4'>
              <div className='text-center mx-auto w-full flex flex-col gap-2'>
                <Text variant='title1' emphasized className='mb-2 mt-3'>
                  Choose an App to build
                </Text>
                <Text variant='body' className='text-sm py-2 max-w-[550px] mx-auto'>
                  Flatfile Apps streamline data handling from basic CSV imports to advanced enterprise tasks. Choose an
                  App that fits your needs or create your own to begin tackling your data challenges.
                </Text>
              </div>
            </div>

            <div className='flex-grow overflow-y-auto py-4'>
              <div className='px-6 w-full flex justify-center'>
                <div className='overflow-x-auto max-w-full'>
                  <CardView />
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {!hideUI &&
        (mini ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                className='inline-flex items-center justify-center w-5 h-5 rounded border border-input bg-transparent'
                onClick={toggleModal}
                type='button'
              >
                <Icon name='plus-small' size={16} />
              </button>
            </TooltipTrigger>
            <TooltipContent className='flex items-center px-2 py-1'>
              <Text variant='body' className='text-sm'>
                Add an App
              </Text>
            </TooltipContent>
          </Tooltip>
        ) : (
          <Button size='md' slotLeft={<Icon name='plus-small' size={16} />} onClick={toggleModal}>
            New App
          </Button>
        ))}
    </>
  )
}
